import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Button, Alert } from '@mui/material';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import RefreshIcon from '@mui/icons-material/Refresh';
import { DATA_VERSION } from '../../constants/appVersion';
import { indexedDbService, STORES } from '../../services/indexedDbService';

interface SwInfo {
  activeState: string;
  activeScriptURL: string;
  waitingState: string;
  cacheName: string | null;
  cacheEntryCount: number;
  appointmentCount: number;
}

const AppInfoTab: React.FC = () => {
  const [swInfo, setSwInfo] = useState<SwInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [clearing, setClearing] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [clearResult, setClearResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [refreshResult, setRefreshResult] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const gather = useCallback(async () => {
    try {
      const info: SwInfo = {
        activeState: 'Not registered',
        activeScriptURL: '',
        waitingState: '',
        cacheName: null,
        cacheEntryCount: 0,
        appointmentCount: 0,
      };

      if ('serviceWorker' in navigator) {
        const reg = await navigator.serviceWorker.getRegistration();
        if (reg) {
          if (reg.active) {
            info.activeState = reg.active.state;
            info.activeScriptURL = reg.active.scriptURL;
          } else {
            info.activeState = 'Registered but no active worker';
          }
          if (reg.waiting) {
            info.waitingState = `${reg.waiting.state} (${reg.waiting.scriptURL})`;
          }
        }
      }

      if ('caches' in window) {
        const names = await caches.keys();
        const mindshareCache = names.find((n) => n.startsWith('mindshare-cache'));
        if (mindshareCache) {
          info.cacheName = mindshareCache;
          const cache = await caches.open(mindshareCache);
          const keys = await cache.keys();
          info.cacheEntryCount = keys.length;
        }
      }

      try {
        const appointments = await indexedDbService.getAllAppointments();
        info.appointmentCount = appointments.length;
      } catch {
        // DB may not be initialized yet
      }

      setSwInfo(info);
    } catch (e) {
      console.error('Error gathering app info:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    gather();
  }, [gather]);

  const handleClearAppointments = async () => {
    setClearing(true);
    setClearResult(null);
    try {
      await indexedDbService.clearAppointmentCache();
      await indexedDbService.clearStore(STORES.APPOINTMENT_EXCEPTIONS);
      await indexedDbService.clearStore(STORES.SYNC_METADATA);
      setClearResult({ type: 'success', message: 'Appointment cache cleared. Refresh the calendar to reload from the server.' });
      await gather();
    } catch (e) {
      console.error('Error clearing appointment cache:', e);
      setClearResult({ type: 'error', message: 'Failed to clear appointment cache.' });
    } finally {
      setClearing(false);
    }
  };

  const handleForceRefreshSW = async () => {
    setRefreshing(true);
    setRefreshResult(null);
    try {
      if (!('serviceWorker' in navigator)) {
        setRefreshResult({ type: 'error', message: 'Service workers are not supported in this browser.' });
        return;
      }

      const reg = await navigator.serviceWorker.getRegistration();
      if (!reg) {
        setRefreshResult({ type: 'error', message: 'No service worker registration found.' });
        return;
      }

      // If there's a waiting worker, skip waiting to activate it
      if (reg.waiting) {
        reg.waiting.postMessage({ type: 'SKIP_WAITING' });
        setRefreshResult({ type: 'success', message: 'Activated waiting service worker. The page will reload shortly.' });
        return;
      }

      // Otherwise, force an update check and unregister/re-register to get a clean slate
      await reg.unregister();
      setRefreshResult({ type: 'success', message: 'Service worker unregistered. Reloading page...' });
      setTimeout(() => window.location.reload(), 1000);
    } catch (e) {
      console.error('Error refreshing service worker:', e);
      setRefreshResult({ type: 'error', message: 'Failed to refresh service worker.' });
    } finally {
      setRefreshing(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        App Info
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <InfoRow label="Data Version" value={String(DATA_VERSION)} />
        <InfoRow
          label="Active Service Worker"
          value={swInfo?.activeState ?? 'Unknown'}
        />
        {swInfo?.waitingState && (
          <InfoRow label="Waiting Worker" value={swInfo.waitingState} />
        )}
        <InfoRow
          label="Cache Name"
          value={swInfo?.cacheName ?? 'No cache found'}
        />
        <InfoRow
          label="Cached Entries"
          value={String(swInfo?.cacheEntryCount ?? 0)}
        />
        <InfoRow
          label="Cached Appointments"
          value={String(swInfo?.appointmentCount ?? 0)}
        />
      </Box>

      <Box sx={{ mt: 3 }}>
        {clearResult && (
          <Alert severity={clearResult.type} sx={{ mb: 2 }}>
            {clearResult.message}
          </Alert>
        )}
        {refreshResult && (
          <Alert severity={refreshResult.type} sx={{ mb: 2 }}>
            {refreshResult.message}
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleClearAppointments}
            disabled={clearing}
          >
            {clearing ? 'Clearing...' : 'Clear Appointment Cache'}
          </Button>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RefreshIcon />}
            onClick={handleForceRefreshSW}
            disabled={refreshing}
          >
            {refreshing ? 'Refreshing...' : 'Force Refresh Service Worker'}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Box>
);

export default AppInfoTab;
